import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { ConnectBYOApps200Response } from '../models/ConnectBYOApps200Response';
import { ConnectBYOAppsRequest } from '../models/ConnectBYOAppsRequest';

/**
 * no description
 */
export class AzureBYOAApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * Connect BYOA (Bring Your Own Apps) Azure applications and setup scanning
   * @param projectId The project ID
   * @param connectBYOAppsRequest 
   */
  public async connectBYOApps(projectId: string, connectBYOAppsRequest: ConnectBYOAppsRequest, _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("AzureBYOAApi", "connectBYOApps", "projectId");
    }


    // verify required parameter 'connectBYOAppsRequest' is not null or undefined
    if (connectBYOAppsRequest === null || connectBYOAppsRequest === undefined) {
      throw new RequiredError("AzureBYOAApi", "connectBYOApps", "connectBYOAppsRequest");
    }


    // Path Params
    const localVarPath = '/projects/{projectId}/azure/byoa/connect'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")


    // Body Params
    const contentType = "application/json"
    requestContext.setHeaderParam("Content-Type", contentType);
    const serializedBody = JSON.stringify(ObjectSerializer.serialize(connectBYOAppsRequest, "ConnectBYOAppsRequest", ""));
    requestContext.setBody(serializedBody);

    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class AzureBYOAApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to connectBYOApps
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async connectBYOAppsWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ConnectBYOApps200Response >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ConnectBYOApps200Response = ObjectSerializer.deserialize(
        await response.body.json(),
        "ConnectBYOApps200Response", ""
      ) as ConnectBYOApps200Response;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("400", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Bad request - Invalid input (e.g., invalid UUID format, invalid secret URL)", undefined, response.headers, response);
    }
    if (isCodeInRange("404", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Project not found or no KV Access App found on EON tenant", undefined, response.headers, response);
    }
    if (isCodeInRange("500", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Internal server error", undefined, response.headers, response);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ConnectBYOApps200Response = await response.body.json() as ConnectBYOApps200Response;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
